import React, { useCallback, useState } from 'react';
import styles from './PropertySetAffiliateStepper.scss';
import Stepper, { Step } from './Stepper/index';
import useUser from '../stores/useUser';
// import adformTrackApi from '../utils/adformTrackApi';
import Affiliate, { IBankAffiliateList } from '../utils/Affiliate';
import IHome from '../interfaces/IHome';
import UserRequest, { IUserStatus } from '../utils/UserRequest';
import Select from './MaterialUi/Select';
import Config from '../utils/Config';
import TextField from './MaterialUi/TextField';
import { createModal } from '../utils/CreateModal';

export interface IPropertySetAffiliateStepperProps {
    closeHandler: () => void;
    id: string;
    needsPremium: boolean;
    marketingType: IHome['marketingType'];
}

export interface IPropertySetAffiliateStepperState {
    userData: IUserStatus;
    message: string;
    isSubmitting: boolean;
}

const userApiUrl = Config.get('userApi.url');

const PropertySetAffiliateStepper: React.FC<React.PropsWithChildren<IPropertySetAffiliateStepperProps>> = ({ id = '', closeHandler = () => { }, needsPremium = false, marketingType }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [errorList, setErrorList] = useState([]);
    const [user, userActions] = useUser();
    const [affiliateList, setAffiliateList] = useState<IBankAffiliateList[]>([]);
    const [affiliatePartnerId, setAffiliatePartnerId] = useState("");
    const [possibleAffiliate, setPossibleAffiliate] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [affiliateName, setAffiliateName] = useState("");
    const [complete, setComplete] = useState(false);

    React.useEffect(() => {
        userActions.getStatus().catch((e) => {
            console.error(e);
        });
    }, []);

    React.useEffect(() => {
        Affiliate.getAllBankAffiliates().then((list) => {
            setAffiliateList(list);
        });
    }, []);

    React.useEffect(() => {
        if (complete) {
            openMessagePropertyModal(affiliatePartnerId);
        }
    }, [complete]);

    const onSelectChange = (ev) => {
        setPossibleAffiliate(ev.target.value);
    }

    const onPostalCodeChange = (ev) => {
        setPostalCode(ev.target.value);

        const errorList = [];
        const regex = new RegExp(/(^\d*$)|(Backspace|Tab|Delete|ArrowLeft|ArrowRight)/);

        if (ev.target.value == '') {
            errorList.push({ id: 'postalcode', text: 'Bitte gib deine Postleitzahl ein.', fields: ['postal_code'] });
        } else if (!ev.target.value.match(regex)) {
            errorList.push({ id: 'postalcode', text: 'Die Postleitzahl darf ausschließlich aus Zahlen bestehen.', fields: ['postal_code'] });
        } else if (ev.target.value.length < 5 || ev.target.value.length > 5) {
            errorList.push({ id: 'postalcode', text: 'Die Postleitzahl muss aus genau 5 Zahlen bestehen.', fields: ['postal_code'] });
        }

        if (errorList.length > 0) {
            setErrorList(errorList);
            return;
        } else {
            searchAffiliate(ev.target.value);
        }
    }

    const onSubmitSelection = (ev: React.FormEvent) => {
        const partnerId = affiliateList.find((a) => a.affiliate_name === possibleAffiliate)?.uuid;
        setAffiliatePartnerId(partnerId);
        if (partnerId) {
            //save affiliate already in profile, when user is logged in
            if (user.isLoggedIn) {
                saveAffiliate(ev, partnerId);
                setComplete(true);
            } else {
                setComplete(true);
                return;
            }
        }
    };

    const onSubmitPostcode = (ev: React.FormEvent) => {
        setIsLoading(true);
        const partnerId = affiliateList.find((a) => a.uuid === affiliatePartnerId)?.uuid;
        setAffiliatePartnerId(partnerId);
        if (partnerId) {
            //save affiliate already in profile, when user is logged in
            if (user.isLoggedIn) {
                saveAffiliate(ev, partnerId);
                setComplete(true);
            } else {
                setComplete(true);
                return;
            }
        }
    };

    const saveAffiliate = (ev: React.FormEvent, partnerId) => {
        setIsLoading(true);
        const name = affiliateList.find((a) => a.uuid === partnerId)?.affiliate_name || '';
        Affiliate.saveAffiliate({ name }, user.uid).catch((e) => {
            console.error(e);
        });
    }

    const searchAffiliate = (postalCode) => {
        setAffiliateName(null);
        const errorList = [];
        setIsLoading(true);
        return UserRequest.postRequest(userApiUrl + `/prop-api/econ/win-plz-abfrage`, {
            plz: postalCode,
        })
            .then((res) => {
                // success
                const data = res.getData();
                const partnerId = affiliateList.find((a) => a.affiliate_name === ((data.data.kundennummer).toLowerCase()))?.uuid;
                setAffiliatePartnerId(partnerId);
                if (partnerId) {
                    setErrorList([]);
                    const displayName = affiliateList.find((a) => a.uuid === partnerId)?.display_name || '';
                    setAffiliateName(displayName);
                    setIsLoading(false);
                } else {
                    errorList.push({ id: 'postalcode', text: 'Es konnte keine passende Bank zu deiner Postleitzahl gefunden werden.', fields: ['postalcode'] });
                    setErrorList(errorList);
                    setIsLoading(false);
                }
            })
            .catch((e) => {
                // reset state on error
                errorList.push({ id: 'postalcode', text: 'Bei der Suche nach einer passenden Bank ist ein Fehler aufgetreten. Bitte probiere es später noch einmal.', fields: ['postal_code'] });
                setErrorList(errorList);
                setIsLoading(false);
                return Promise.reject(e);
            });
    };

    const openMessagePropertyModal = (partnerId) => {
        closeHandler();
        createModal.createPropertyMessage(id, needsPremium, marketingType, partnerId);
    }

    const trackUrl = window.location.pathname + '?modal=addPropertyMessage';

    return (
        <div className={styles.propertySetAffiliateStepper}>
            <Stepper>
                <Step title="Wohnen in Niederbayern" trackPageviewUrl={`${trackUrl}&step=set_affiliate`} buttonText="Auswahl bestätigen" nextHandler={onSubmitSelection}>
                    <div className={styles.setAffiliateForm}>
                        <p><b>Herzlich willkommen!</b> Wohnglück.de agiert für Wohnen-in-Niederbayern als Immobiliensuchmaschine.</p>
                        <p>Damit dich die teilnehmenden Banken bestmöglich im weiteren Prozess unterstützen können, wähle bitte deine Hausbank aus. Wenn du noch keine Hausbank hast, wähle gerne eine beliebige Bank aus, oder teile uns deine Postleitzahl mit, dann wird dir die passende Bank zugeordnet.</p>
                        <Select onChange={onSelectChange} name="hausbank" value={possibleAffiliate} label="Wähle deine Hausbank" required>
                            <option value="rb-arnstorf">Raiffeisenbank Arnstorf eG</option>
                            <option value="rb-deggendorf">Raiffeisenbank eG Deggendorf-Plattling-Sonnenwald</option>
                            <option value="rb-goldenersteig">Raiffeisenbank Goldener Steig eG – Dreisessel</option>
                            <option value="rb-parkstetten">Raiffeisenbank Parkstetten eG</option>
                            <option value="rb-straubing">Raiffeisenbank Straubing eG</option>
                            <option value="vr-rottalinn">VR Bank Rottal-Inn eG</option>
                            <option value="vr-donauwald">VR GenoBank DonauWald eG</option>
                            <option value="keine">Ich habe keine Hausbank</option>
                        </Select>
                    </div>
                </Step>
                {possibleAffiliate == "keine" &&
                    <Step title="Deine Postleitzahl" trackPageviewUrl={`${trackUrl}&step=set_postcode`} nextHandler={onSubmitPostcode} isSubmitting={isLoading}>
                        <div className={styles.setAffiliateForm}>
                            <p>Bitte gib deine Postleitzahl ein:</p>
                            <div>
                                <TextField
                                    name="postal_code"
                                    value={postalCode}
                                    onChange={onPostalCodeChange}
                                    label="Postleitzahl"
                                    required={true}
                                />
                                {errorList
                                    .filter((e) => e.id === 'postalcode')
                                    .map((e) => (
                                        <p className={styles.error} key={e.text}>
                                            {e.text}
                                        </p>
                                    ))}
                            </div>
                            {affiliateName &&
                                <>
                                    <p>Auf Basis der regionalen Nähe empfehlen wir dir die folgende Bank:</p>
                                    <p><b>{affiliateName}</b></p>
                                    <p>Mit Klick auf “Weiter” hinterlegen wir diese Bank in deinem Profil. Du kannst sie später dort jederzeit ändern.</p>
                                </>
                            }
                        </div>
                    </Step>
                }
            </Stepper>
        </div>
    );
};

export default PropertySetAffiliateStepper;